<template>
  <div class="encounter-start-workspace">
    <LoadingDiv v-if="!isReady" />
    <template v-else>
      <section class="encounter-start-picker" :aria-busy="isLoading">
        <div class="encounter-start-heading">
          <h2>Find an enrollee</h2>
          <p>Enter an enrollee ID and choose the coverage month to start a visit.</p>
        </div>
        <form class="encounter-start-form" @submit.prevent="handleSearch">
          <label for="encounter-start-id">Enrollee ID</label>
          <div class="encounter-start-controls">
            <div class="encounter-start-input">
              <i class="material-icons" aria-hidden="true">search</i>
              <input id="encounter-start-id" class="browser-default" type="search" v-model="enrolleeId" placeholder="Enter enrollee ID" autocomplete="off" :disabled="isLoading" required />
              <button v-if="enrolleeId || enrolleeGroup.length || error" type="button" class="encounter-start-clear" @click="handleClear" :disabled="isLoading" aria-label="Clear enrollee search"><i class="material-icons" aria-hidden="true">close</i></button>
            </div>
            <button class="encounter-start-submit" type="submit" :disabled="isLoading || !enrolleeId"><i class="material-icons" aria-hidden="true">{{ isLoading ? 'hourglass_empty' : 'search' }}</i>{{ isLoading ? 'Searching…' : 'Search' }}</button>
          </div>
          <div class="progress encounter-search-progress" v-if="isLoading"><div class="indeterminate"></div></div>
        </form>
        <div class="encounter-start-periods" role="group" aria-label="Coverage month">
          <span>Coverage month</span>
          <div class="encounter-start-month-options">
            <button v-for="period in [searchCurr, searchPrev]" :key="period" type="button" :aria-pressed="searchPeriod === period" :class="{ 'is-active': searchPeriod === period }" :disabled="isLoading" @click="searchPeriod = period">
              <i class="material-icons" aria-hidden="true">{{ searchPeriod === period ? 'check_circle' : 'calendar_today' }}</i>
              {{ new Date(period).toLocaleString(undefined, { year: 'numeric', month: 'long' }) }}
            </button>
          </div>
        </div>
      </section>
      <Transition name="encounter-error-fade"><div v-if="error" class="encounter-start-feedback" role="alert"><i class="material-icons" aria-hidden="true">error_outline</i><span>{{ error }}</span></div></Transition>
      <div class="encounter-start-results" v-if="enrolleeGroup.length">
        <p>Choose the enrollee to continue.</p>
        <button class="encounter-start-result" v-for="enrollee in enrolleeGroup" :key="enrollee.id" type="button" @click="handleStart(enrollee)">
          <span><strong>{{ capWords(enrollee?.name) }}</strong><small>{{ enrollee?.enrollee_number }} · {{ enrollee?.scheme?.name }}</small></span>
          <i class="material-icons" aria-hidden="true">arrow_forward</i>
        </button>
      </div>
    </template>
  </div>
</template>
<script>
import { nextTick, ref, onMounted, watch, watchEffect } from "vue";
import { useStore } from "vuex";
import { capWordsFx } from "@/composables/fxLibrary/coreFx";
import { HMO } from '@/laravel/config';
import LoadingDiv from "@/components/LoadingDiv.vue";
import getItemsOfItems from "@/composables/getItemsOfItems";
import { getCurr, getPrev } from "@/composables/encounter/eligible";
import { getFullMonthName } from "@/composables/fxLibrary/months";
import { normalizeEnrolleeSearchValue } from "@/composables/enrolleeNumber";

export default {
  name: "StartAuthComponent",
  props: ["serverTime", "initialSearch"],
  components: { LoadingDiv },
  emits: ["next"],
  setup(props, context) {
    const isReady = ref(false);
    const response = ref(null);
    const enrolleeGroup = ref([]);
    const enrolleeId = ref(null);
    const serverDate = ref(null);
    const searchPeriod = ref(null);
    const searchCurr = ref(null);
    const searchPrev = ref(null);
    const error = ref(false);
    const isLoading = ref(false);
    const endPoint = ref(null);
    const callFunction = ref(null);
    const lastInitialSearch = ref(null);

    // store
    const store = useStore();

    // config
    const providerId = store.state?.user?.account?.id;

    // watch 
    watch(() => searchPeriod.value, () => {
    handleClear();
    }
    );

    const setupSearchPeriods = () => {
      const currentDate = props.serverTime?.current_date_full;

      if (!currentDate) {
        return false;
      }

      searchPeriod.value = getCurr(currentDate);
      searchCurr.value = getCurr(currentDate);
      searchPrev.value = getPrev(currentDate);
      isReady.value = true;
      isLoading.value = false;

      return true;
    };

    // load endpoint
    onMounted(() => {
      setupSearchPeriods();
    })

    // load endpoint
    onMounted(() => {
       // default endpoint
        endPoint.value = "operator/enrollee/encounter/checker";
        // default call function
        callFunction.value = getItemsOfItems;
    })

    //from functions
    const capWords = (val) => {
      return capWordsFx(val);
    };

    // get function
    const getSearchItemsFromApi = (table, match) => {
      // loading starts
      isLoading.value = true;
      // clear error
      error.value = null;

      (async () => {
        const { res } = await callFunction.value(table, match);

        if (res.value && res.value.status) {
          // set server date
          serverDate.value = res.value.server;
          // display options
          if (res.value.data.length === 1) {
            response.value = res.value.data[0];
          } else {
            enrolleeGroup.value = res.value.data;
          }
        } else {
          let date = searchPeriod.value.split("-");
          error.value = `Sorry, Enrollee not on ${HMO} ${getFullMonthName(date[1] - 1)} List`;
          response.value = null; // clear standing response
        }

        // page is ready, loading is complete
        isLoading.value = false;
      })();
    };

    // search
    const handleSearch = () => {
      // loading starts
      if (enrolleeId.value) {
      // start loading
      isLoading.value = true;
      // val
      let date = searchPeriod.value.split("-");
      // val
      let val = `?y=${date[0]}&m=${date[1]}&q=${encodeURIComponent(normalizeEnrolleeSearchValue(enrolleeId.value))}`;
      // search: 0 = provider_id // serach all providers
      getSearchItemsFromApi(endPoint.value, `${providerId}${val}`);
      }
    };

    const runInitialSearch = async () => {
      const initialSearch = normalizeEnrolleeSearchValue(props.initialSearch);

      if (!initialSearch || lastInitialSearch.value === initialSearch || !isReady.value || !endPoint.value || !callFunction.value || !searchPeriod.value) {
        return;
      }

      await nextTick();

      lastInitialSearch.value = initialSearch;
      enrolleeId.value = initialSearch;
      handleSearch();
    };

    // start
    const handleStart = (val) => {
      // set
      response.value = val;
    }

    // clear
    const handleClear = () => {
      enrolleeGroup.value = [];
      enrolleeId.value = null;
      response.value = null;
      error.value = false;
    };

    onMounted(() => {
      runInitialSearch();
    });

    watch(() => props.initialSearch, () => {
      lastInitialSearch.value = null;
      runInitialSearch();
    });

    watch(() => props.serverTime?.current_date_full, () => {
      if (setupSearchPeriods()) {
        runInitialSearch();
      }
    });

    // next
    watchEffect(() => {
      if (response.value) {
        context.emit("next", {
          value: response.value,
          serverDate: serverDate.value,
          searchPeriod: searchPeriod.value
        });
      }
    });

    return {
      capWords,
      handleStart,
      handleSearch,
      handleClear,
      error,
      isLoading,
      isReady,
      searchPeriod,
      searchCurr,
      searchPrev,
      enrolleeGroup,
      enrolleeId,
      payer: HMO,
    };
  },
};
</script>

<style scoped>
@layer components {
  .encounter-start-workspace { min-width: 0; width: 100%; }
  .encounter-start-workspace .encounter-start-picker { background: var(--ui-card); border: 1px solid var(--ui-border); border-radius: var(--ui-radius); box-sizing: border-box; padding: 1.5rem; }
  .encounter-start-workspace .encounter-start-heading h2 { color: var(--ui-foreground); font-size: 1.125rem; font-weight: 600; margin: 0 0 .4rem; }
  .encounter-start-workspace .encounter-start-heading p { color: var(--ui-muted-foreground); font-size: .8125rem; line-height: 1.6; margin: 0 0 1.5rem; }
  .encounter-start-workspace .encounter-start-form { background: transparent !important; border: 0 !important; box-shadow: none !important; margin: 0 !important; padding: 0 !important; position: relative; }
  .encounter-start-workspace .encounter-start-form > label { color: var(--ui-foreground); display: block; font-size: .75rem; font-weight: 500; margin-bottom: .5rem; }
  .encounter-start-controls { align-items: center; display: flex; gap: .75rem; }
  .encounter-start-input { flex: 1; min-width: 0; position: relative; }
  .encounter-start-input > i { color: var(--ui-muted-foreground); font-size: 1.125rem; left: .8rem; pointer-events: none; position: absolute; top: .8rem; }
  .encounter-start-workspace .encounter-start-input input { background: var(--ui-background) !important; border: 1px solid var(--ui-input) !important; border-radius: calc(var(--ui-radius) - 2px) !important; box-sizing: border-box; color: var(--ui-foreground); font: inherit; font-size: .875rem; height: 2.75rem !important; margin: 0 !important; padding: 0 2.5rem !important; width: 100% !important; }
  .encounter-start-clear { align-items: center; background: transparent; border: 0; color: var(--ui-muted-foreground); cursor: pointer; display: flex; height: 2.25rem; justify-content: center; position: absolute; right: .25rem; top: .25rem; width: 2.25rem; }
  .encounter-start-clear i { font-size: 1rem; }
  .encounter-start-submit { align-items: center; background: var(--ui-primary); border: 1px solid var(--ui-primary); border-radius: calc(var(--ui-radius) - 2px); color: var(--ui-primary-foreground); cursor: pointer; display: inline-flex; flex: 0 0 auto; font: inherit; font-size: .8125rem; font-weight: 600; gap: .5rem; min-height: 2.75rem; padding: .5rem 1rem; }
  .encounter-start-submit i { font-size: 1rem; }
  .encounter-start-periods { align-items: center; border-top: 1px solid var(--ui-border); display: flex; flex-wrap: wrap; gap: .75rem 1.5rem; margin-top: 1.5rem; padding-top: 1.25rem; }
  .encounter-start-periods > span { color: var(--ui-muted-foreground); font-size: .75rem; }
  .encounter-start-month-options { display: flex; flex-wrap: wrap; gap: .5rem; }
  .encounter-start-month-options button { align-items: center; background: var(--ui-background); border: 1px solid var(--ui-border); border-radius: calc(var(--ui-radius) - 2px); color: var(--ui-muted-foreground); cursor: pointer; display: inline-flex; font: inherit; font-size: .8125rem; gap: .5rem; min-height: 2.25rem; padding: .4rem .75rem; }
  .encounter-start-month-options button.is-active { background: var(--ui-secondary); border-color: var(--ui-primary); color: var(--ui-foreground); }
  .encounter-start-month-options i { font-size: 1rem; }
  .encounter-start-workspace .encounter-search-progress { bottom: -.5rem; height: 2px; }
  .encounter-start-feedback { align-items: center; background: color-mix(in srgb, var(--ui-destructive) 6%, var(--ui-background)); border: 1px solid color-mix(in srgb, var(--ui-destructive) 25%, var(--ui-border)); border-radius: var(--ui-radius); color: var(--ui-destructive); display: flex; font-size: .8125rem; gap: .75rem; margin-top: 1rem; padding: 1rem; }
  .encounter-start-feedback i { font-size: 1.125rem; }
  .encounter-start-results > p { color: var(--ui-muted-foreground); font-size: .8125rem; margin: 1.25rem 0 .75rem; }
  .encounter-start-result { align-items: center; background: var(--ui-card); border: 1px solid var(--ui-border); border-radius: var(--ui-radius); color: var(--ui-foreground); cursor: pointer; display: flex; font: inherit; gap: 1rem; justify-content: space-between; margin-top: .5rem; padding: 1rem; text-align: left; width: 100%; }
  .encounter-start-result strong { display: block; font-size: .875rem; font-weight: 600; }
  .encounter-start-result small { color: var(--ui-muted-foreground); display: block; font-size: .75rem; margin-top: .3rem; }
  .encounter-start-workspace button:disabled { cursor: not-allowed; opacity: .55; }
  .encounter-start-workspace :is(button, input):focus-visible { outline: 2px solid var(--ui-ring); outline-offset: 2px; }
  @media (max-width: 600px) { .encounter-start-controls { align-items: stretch; flex-direction: column; } .encounter-start-submit { justify-content: center; } .encounter-start-workspace .encounter-start-picker { padding: 1.25rem; } }
}
.encounter-start-search-shell {
  position: relative;
}

.progress.encounter-search-progress {
  border-radius: 0 0 4px 4px;
  bottom: -4px;
  height: 4px;
  left: 0;
  margin: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  width: 100%;
  z-index: 2;
}

.encounter-error-fade-enter-active,
.encounter-error-fade-leave-active {
  overflow: hidden;
  transition: opacity 0.18s ease, transform 0.18s ease, max-height 0.18s ease;
}

.encounter-error-fade-enter-from,
.encounter-error-fade-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-6px);
}

.encounter-error-fade-enter-to,
.encounter-error-fade-leave-from {
  max-height: 56px;
  opacity: 1;
  transform: translateY(0);
}
</style>
